import { confirmar } from '@/components/ui/confirm-dialog';
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { EncabezadoDeGestion } from '@/features/cotiza/components/encabezado-de-gestion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, FileText, FileSignature, Copy, Trash2, Eye } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { contractService } from '../services/contract.service';
import type { Contract, ContractStatus, ContractType } from '../types/contract.types';
import { formatDateString } from '@/lib/utils/format';

// ── Labels ──────────────────────────────────────────────────────

const STATUS_LABELS: Record<ContractStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  EXPIRED: 'Vencido',
  CANCELLED: 'Cancelado',
};

const STATUS_VARIANTS: Record<ContractStatus, 'secondary' | 'success' | 'destructive' | 'outline'> =
  {
    DRAFT: 'secondary',
    ACTIVE: 'success',
    EXPIRED: 'destructive',
    CANCELLED: 'outline',
  };

const DIA_MS = 24 * 60 * 60 * 1000;

/**
 * La vigencia como la maqueta: «01/03/2026 – 28/02/2027» y debajo cuánto falta
 * («vence en 40 días», «venció hace 35 días»). Un contrato vigente que vence en
 * menos de 60 días se marca: es el que hay que renovar.
 */
function Vigencia({ desde, hasta }: { desde?: string | null; hasta?: string | null }) {
  if (!desde && !hasta) return <span className="text-muted-foreground">—</span>;
  const fecha = (iso?: string | null) => (iso ? formatDateString(iso) : '…');
  let resto: { texto: string; urgente: boolean } | null = null;
  if (hasta) {
    const dias = Math.round((new Date(hasta).getTime() - Date.now()) / DIA_MS);
    if (dias < 0)
      resto = { texto: `venció hace ${-dias} ${-dias === 1 ? 'día' : 'días'}`, urgente: false };
    else if (dias === 0) resto = { texto: 'vence hoy', urgente: true };
    else resto = { texto: `vence en ${dias} ${dias === 1 ? 'día' : 'días'}`, urgente: dias <= 60 };
  }
  return (
    <div className="whitespace-nowrap text-sm">
      <p>
        {fecha(desde)} – {fecha(hasta)}
      </p>
      {resto && (
        <p
          className={
            resto.urgente ? 'text-xs font-medium text-alerta' : 'text-xs text-muted-foreground'
          }
        >
          {resto.texto}
        </p>
      )}
    </div>
  );
}

const TYPE_LABELS: Record<ContractType, string> = {
  CONTRACT: 'Contrato',
  TERMS: 'Términos',
  PRIVACY: 'Privacidad',
  AMENDMENT: 'Adenda',
  OTHER: 'Otro',
};

// ── Page ────────────────────────────────────────────────────────

export function ContractsListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [templateFilter, setTemplateFilter] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (typeFilter !== 'ALL') params.type = typeFilter;
      if (templateFilter !== 'ALL') params.isTemplate = templateFilter;

      const result = await contractService.list(params);
      setContracts(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los contratos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, templateFilter, toast]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleDelete = async (id: string) => {
    if (
      !(await confirmar({
        title: '¿Eliminar este contrato?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await contractService.remove(id);
      toast({ title: 'Contrato eliminado' });
      fetchContracts();
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el contrato',
        variant: 'destructive',
      });
    }
  };

  const handleClone = async (id: string) => {
    try {
      const cloned = await contractService.clone(id);
      toast({ title: 'Contrato clonado' });
      navigate(`/contracts/${cloned.id}/edit`);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo clonar el contrato',
        variant: 'destructive',
      });
    }
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/*
          El encabezado de Gestión (artefacto, 19-oct): el paso «Contratos». El de
          antes tenía el título en blanco y no se leía en modo claro.
        */}
        <EncabezadoDeGestion
          activa="contratos"
          titulo="Contratos"
          subtitulo="Lo que se firmó: contratos, plantillas y su documentación."
          acciones={
            <>
              <Button variant="outline" onClick={() => navigate('/contracts/new?template=true')}>
                <FileSignature className="h-4 w-4" aria-hidden="true" />
                Nueva plantilla
              </Button>
              <Button
                variant="degradado"
                size="lg"
                className="px-5"
                onClick={() => navigate('/contracts/new')}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nuevo contrato
              </Button>
            </>
          }
        />

        {/* Filters */}
        <div className="flex gap-3">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los estados</SelectItem>
              <SelectItem value="DRAFT">Borrador</SelectItem>
              <SelectItem value="ACTIVE">Activo</SelectItem>
              <SelectItem value="EXPIRED">Vencido</SelectItem>
              <SelectItem value="CANCELLED">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los tipos</SelectItem>
              <SelectItem value="CONTRACT">Contrato</SelectItem>
              <SelectItem value="TERMS">Términos</SelectItem>
              <SelectItem value="PRIVACY">Privacidad</SelectItem>
              <SelectItem value="AMENDMENT">Adenda</SelectItem>
              <SelectItem value="OTHER">Otro</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={templateFilter}
            onValueChange={(v) => {
              setTemplateFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Plantillas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Contratos y plantillas</SelectItem>
              <SelectItem value="true">Solo plantillas</SelectItem>
              <SelectItem value="false">Solo contratos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Listado de Contratos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>No se encontraron contratos</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate('/contracts/new')}
                >
                  Crear primer contrato
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Vigencia</TableHead>
                    <TableHead>Cotización</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contracts.map((c) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/contracts/${c.id}`)}
                    >
                      <TableCell className="max-w-[320px]">
                        <p className="font-medium">{c.name}</p>
                        {c.isTemplate && (
                          <Badge variant="outline" className="mt-1">
                            Plantilla
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{c.clientName || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{TYPE_LABELS[c.type]}</TableCell>
                      <TableCell>
                        <Vigencia desde={c.validFrom} hasta={c.validTo} />
                      </TableCell>
                      <TableCell>
                        {c.quote ? (
                          <Link
                            to={`/quotes/${c.quote.id}`}
                            className="whitespace-nowrap font-mono text-sm text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.quote.code}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANTS[c.status]}>{STATUS_LABELS[c.status]}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div
                          className="flex justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/contracts/${c.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleClone(c.id)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          {c.status === 'DRAFT' && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)}>
                              <Trash2 className="h-4 w-4 text-bloqueo" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
